import React, { useState, useEffect, useCallback } from "react";
import {
  Menu,
  MenuItem,
  IconButton,
  Icon,
  Badge,
  Avatar,
  Typography,
  Box,
  Stack,
  Tooltip,
  Card,
} from "@mui/material";
import { FixedSizeList as List } from "react-window";
import { useMaterialUIController, setActivePage } from "context";
import loggerFactory from "globals/logger/logger-factory";
import NotificationManager from "globals/websocket/WebSocket";
import { telemetry, application } from "globals/endpoints";
import MDTypography from "components/MDTypography";

const requireImage = require.context(
  "../../../../public/images/serverAlertImages",
  true
);

export default function CTLAlertNotification({ light }) {
  const [alertAnchor, setAlertAnchor] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [severityList, setSeverityList] = useState([]);
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode, transparentNavbar } = controller;

  const notificationSound = new Audio("/sounds/notificationSound.wav");

  const playNotificationSound = useCallback(() => {
    notificationSound.play().catch((error) => {
      console.error("Failed to play notification sound:", error);
    });
  }, []);

  const iconsStyle = useCallback(
    ({ palette: { dark, white, text } }) => ({
      color: darkMode ? white.main : text.main,
    }),
    [darkMode]
  );

  useEffect(() => {
    application
      .post("severity/listAll")
      .then((response) => {
        setSeverityList(response.data.data || []);
      })
      .catch((error) => {
        loggerFactory.error("Unable to fetch severity list", error);
      });
  }, []);

  const imageExists = useCallback((imageName) => {
    try {
      requireImage(`./${imageName}`);
      return true;
    } catch {
      return false;
    }
  }, []);

  const AlertList = useCallback(
    (message) => {
      if (message) {
        const outage = JSON.parse(message.body);
        if (outage?.severity !== 0) playNotificationSound();
      }

      telemetry
        .get("alert/active/list/imageDetails")
        .then((response) => {
          const data = response.data.data || [];
          setMenuItems(data.slice(0, 200)); // limit for performance
        })
        .catch((error) => {
          loggerFactory.error("Unable to fetch Alert list", error);
        });
    },
    [playNotificationSound]
  );

  useEffect(() => {
    AlertList();
    NotificationManager.receiveMessageFor("events", AlertList, "nms-events");
  }, [AlertList]);

  const handleClick = (event) => setAlertAnchor(event.currentTarget);
  const handleClose = () => {
    setAlertAnchor(null);
    const updatedActivePage = {
      key: "dashboard.controller",
      name: "DashboardController",
      url: "/App/DashboardController/Home",
      description: "Dashboard Controller",
    };
    setActivePage(dispatch, updatedActivePage);
  };

  const Row = ({ index, style }) => {
    const item = menuItems[index];
    const severity = severityList.find(
      (sev) => sev.severityLevel === item.severity
    );

    return (
      <div style={style} key={index}>
        <MenuItem
          onClick={handleClose}
          sx={{
            px: 0.75,
            py: 0.5,
            minHeight: "unset",
          }}
        >
          <Card
            sx={{
              width: "100%",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: 0,
              position: "relative",
              px: 1,
              py: 0.5,
              transition: "all 160ms ease",
              "&:hover": {
                backgroundColor: "action.hover",
                borderColor: "primary.main",
                transform: "translateY(-1px)",
              },
            }}
          >
            {severity && (
              <Tooltip title={severity.severityName} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    width: 8,
                    height: "70%",
                    borderRadius: 1,
                    backgroundColor: severity.color || "gray",
                    opacity: 0.95,
                  }}
                />
              </Tooltip>
            )}

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: "100%" }}
            >
              <Avatar
                variant="square"
                loading="lazy"
                src={
                  imageExists(item?.imageName)
                    ? requireImage(`./${item?.imageName}`)
                    : requireImage(`./no_pictures.png`)
                }
                alt={item.name}
                sx={{ width: 34, height: 34, borderRadius: 1.2 }}
              />

              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                <Tooltip
                  placement="left"
                  title={`${item.serviceCode} ${item.name ? " / " : ""} ${
                    item.name
                  }`}
                  arrow
                >
                  <MDTypography
                    variant="subtitle1"
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`${item.serviceCode} ${item.name ? " / " : ""} ${
                      item.name
                    }`}
                  </MDTypography>
                </Tooltip>

                <MDTypography
                  variant="caption"
                  sx={{
                    fontSize: "0.68rem",
                    color: "text",
                    lineHeight: 1.2,
                  }}
                >
                  {new Date(item.alertGeneratedOn * 1000).toLocaleString(
                    "en-US",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                    }
                  )}
                  {" • "}
                  {new Date(item.alertGeneratedOn * 1000).toLocaleDateString(
                    "en-US",
                    {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    }
                  )}
                </MDTypography>
              </Box>
            </Stack>
          </Card>
        </MenuItem>
      </div>
    );
  };

  const itemSize = 72;
  const maxMenuHeight = 480;
  const listHeight = Math.min(maxMenuHeight, menuItems.length * itemSize);

  return (
    <>
      <Tooltip title="Alerts">
        <IconButton color="inherit" onClick={handleClick}>
          <Badge
            badgeContent={menuItems?.length}
            color="error"
            overlap="circular"
            max={999}
          >
            <Icon sx={iconsStyle}>notifications</Icon>
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={alertAnchor}
        open={Boolean(alertAnchor)}
        onClose={() => setAlertAnchor(null)}
        PaperProps={{
          sx: {
            maxHeight: maxMenuHeight,
            width: 340,
            overflow: "hidden",
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 6,
            p: 0.5,
          },
        }}
      >
        {menuItems.length === 0 ? (
          <MenuItem dense>
            <Typography variant="body2">No alerts available</Typography>
          </MenuItem>
        ) : (
          <List
            height={listHeight}
            itemCount={menuItems.length}
            itemSize={itemSize}
            width={340}
          >
            {Row}
          </List>
        )}
      </Menu>
    </>
  );
}
